import type { ReactNode } from "react";
import styles from "./Table.module.css";

/**
 * A single column of a {@link Table}. `render` maps a row to its cell content;
 * `mono` renders the cell in the monospace face (ids, scopes); `align: "end"`
 * right-aligns numeric columns.
 */
export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  mono?: boolean;
  align?: "start" | "end";
}

interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  /** Stable React key per row. */
  getRowKey: (row: T, index: number) => string;
  /** Accessible table caption (rendered, visually subdued). Optional. */
  caption?: ReactNode;
  /** Shown beneath the header when `rows` is empty (e.g. "No entries match"). */
  emptyText?: ReactNode;
  className?: string;
}

/**
 * Shared data-table primitive (Phase 4) — one real semantic `<table>` (thead/tbody,
 * `scope="col"` headers) wrapped in a horizontally-scrollable container so wide
 * tables stay usable on mobile without breaking the layout. Column-config driven so
 * every screen renders tables the same way (audit, per-channel status, …) instead of
 * hand-rolling `<table>` markup each time.
 */
export function Table<T>({ columns, rows, getRowKey, caption, emptyText, className }: TableProps<T>) {
  return (
    <div className={[styles.scroll, className ?? ""].filter(Boolean).join(" ")}>
      <table className={styles.table}>
        {caption ? <caption className={styles.caption}>{caption}</caption> : null}
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} scope="col" className={c.align === "end" ? styles.end : undefined}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={getRowKey(row, i)}>
              {columns.map((c) => {
                const cls = [c.mono ? styles.mono : "", c.align === "end" ? styles.end : ""].filter(Boolean).join(" ");
                return (
                  <td key={c.key} className={cls || undefined}>
                    {c.render(row)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && emptyText ? <p className={styles.empty}>{emptyText}</p> : null}
    </div>
  );
}
